import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bank, Upload, ArrowsClockwise } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { formatCurrency } from "../lib/currency";
import { useAuth } from "../lib/auth";
import { StandardReports } from "../components/StandardReports";

const TABS = ["Cash Position", "Bank Accounts", "Statement Import", "Transactions", "Reports"];

export default function BankingPage() {
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get("tab") || "Cash Position");
  const { user } = useAuth();
  const cur = user?.tenant?.preferred_currency || "OMR";
  const [accounts, setAccounts] = useState([]);
  const reloadAccounts = async () => { const r = await api.get("/banking/accounts"); setAccounts(r.data); };
  useEffect(() => { reloadAccounts().catch(() => {}); }, []);

  return (
    <div className="p-6 sm:p-8" data-testid="banking-page">
      <div className="mb-6">
        <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-400">MODULE / H2H · BANKING</div>
        <h1 className="font-display text-4xl font-black tracking-tighter mt-1 flex items-center gap-3">
          Bank Sync.
          <span className="text-xs font-mono px-2 py-1 bg-[#FF3E00] text-white uppercase tracking-widest">MT940 · camt.053</span>
        </h1>
        <div className="text-sm text-zinc-500 mt-2 max-w-2xl">
          Real-time cash position across every registered bank account. Upload MT940 or camt.053 statements, auto-match to outbound payments and AR invoices, review exceptions.
        </div>
      </div>
      <div className="flex border-b border-zinc-950 mb-0 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`banking-tab-${t.toLowerCase().replace(/\s/g, "-")}`}
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-xs font-semibold tracking-[0.1em] uppercase border-r border-zinc-950 transition-colors whitespace-nowrap ${
              tab === t ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            {t}
          </button>
        ))}
      </div>
      <div className="border-x border-b border-zinc-950 min-h-[60vh]">
        {tab === "Cash Position" && <CashPositionPanel />}
        {tab === "Bank Accounts" && <AccountsPanel accounts={accounts} reload={reloadAccounts} cur={cur} />}
        {tab === "Statement Import" && <ImportPanel accounts={accounts} />}
        {tab === "Transactions" && <TransactionsPanel cur={cur} accounts={accounts} />}
        {tab === "Reports" && <StandardReports module="banking" />}
      </div>
    </div>
  );
}

function CashPositionPanel() {
  const [d, setD] = useState(null);
  const [consolidate, setConsolidate] = useState(false);
  const [fxBusy, setFxBusy] = useState(false);
  const load = () => api.get(`/banking/cash-position${consolidate ? "?consolidate=true" : ""}`).then((r) => setD(r.data));
  useEffect(() => { load().catch(() => {}); }, [consolidate]);  // eslint-disable-line react-hooks/exhaustive-deps

  const refreshFx = async () => {
    setFxBusy(true);
    try {
      const r = await api.post("/fx/refresh");
      if (r.data.ok) { toast.success(`FX refreshed · ${r.data.rows} rows as of ${r.data.as_of}`); await load(); }
      else toast.error(r.data.reason || "Refresh failed");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setFxBusy(false); }
  };

  if (!d) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;
  const currencies = Object.keys(d.totals_by_currency || {});
  const gridColsMap = { 1: "md:grid-cols-1", 2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-4", 5: "md:grid-cols-5" };
  const gridColsClass = gridColsMap[Math.min(currencies.length || 1, 5)] || "md:grid-cols-2";
  return (
    <div className="p-6" data-testid="cash-position-panel">
      <div className="mb-4 flex justify-between items-baseline">
        <div className="text-xs font-mono text-zinc-400">CASH POSITION · {new Date(d.as_of).toLocaleString()}</div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-[10px] font-mono cursor-pointer" data-testid="consolidate-toggle-label">
            <input type="checkbox" checked={consolidate} onChange={(e) => setConsolidate(e.target.checked)} data-testid="consolidate-toggle" />
            CONSOLIDATE TO {d.reporting_currency}
          </label>
          <button onClick={refreshFx} disabled={fxBusy} className="text-[10px] font-mono border border-zinc-950 px-3 py-1 hover:bg-zinc-950 hover:text-white flex items-center gap-1 disabled:opacity-30" data-testid="fx-refresh-btn">
            <ArrowsClockwise size={10} /> {fxBusy ? "FX…" : "REFRESH FX"}
          </button>
          <button onClick={load} className="text-[10px] font-mono border border-zinc-950 px-3 py-1 hover:bg-zinc-950 hover:text-white flex items-center gap-1">
            <ArrowsClockwise size={10} /> REFRESH
          </button>
        </div>
      </div>

      {consolidate && d.consolidated && (
        <div className="border-2 border-[#FF3E00] bg-orange-50 p-5 mb-5" data-testid="consolidated-card">
          <div className="font-mono text-[10px] tracking-widest text-[#FF3E00] mb-3">CONSOLIDATED · REPORTING CURRENCY {d.reporting_currency}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><div className="label-sm text-zinc-500">Cash</div><div className="font-display font-black text-2xl tnum">{formatCurrency(d.consolidated.cash_total_reporting, d.reporting_currency)}</div></div>
            <div><div className="label-sm text-zinc-500">Outstanding AR</div><div className="font-display font-black text-2xl tnum text-emerald-700">{formatCurrency(d.consolidated.outstanding_ar_reporting, d.reporting_currency)}</div></div>
            <div><div className="label-sm text-zinc-500">Outstanding AP</div><div className="font-display font-black text-2xl tnum text-[#D0021B]">{formatCurrency(d.consolidated.outstanding_ap_reporting, d.reporting_currency)}</div></div>
            <div><div className="label-sm text-zinc-500">Net Position</div><div className="font-display font-black text-2xl tnum">{formatCurrency(d.consolidated.net_cash_position_reporting, d.reporting_currency)}</div></div>
          </div>
          <div className="mt-3 text-[10px] font-mono text-zinc-500">
            FX sources: {d.consolidated.fx_sources.map((f) => `${f.pair} @ ${f.rate.toFixed(4)} (${f.source})`).join(" · ")}
          </div>
        </div>
      )}

      <div className={`grid gap-px bg-zinc-200 border border-zinc-200 mb-5 grid-cols-1 ${gridColsClass}`}>
        {currencies.length === 0 ? (
          <div className="bg-white p-5 col-span-full text-sm text-zinc-400">No bank accounts yet. Add one on the Bank Accounts tab.</div>
        ) : currencies.map((c) => (
          <div key={c} className="bg-white p-5">
            <div className="label-sm text-zinc-500">{c} BALANCE</div>
            <div className="font-display font-black text-3xl tnum mt-1">{formatCurrency(d.totals_by_currency[c], c)}</div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-5">
        <OutstandingCard label="Outstanding AR" data={d.outstanding_ar_by_currency} tone="emerald" />
        <OutstandingCard label="Outstanding AP" data={d.outstanding_ap_by_currency} tone="red" />
      </div>

      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-xs uppercase">
            <th className="text-left px-3 py-2">Account</th>
            <th className="text-left px-3 py-2">Bank</th>
            <th className="text-left px-3 py-2">IBAN</th>
            <th className="text-right px-3 py-2">Balance</th>
            <th className="text-left px-3 py-2">Last Statement</th>
          </tr>
        </thead>
        <tbody data-testid="cash-position-tbody">
          {d.accounts.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-zinc-400 text-sm">No accounts.</td></tr>}
          {d.accounts.map((a) => (
            <tr key={a.id} className="border-b border-zinc-100">
              <td className="px-3 py-2">{a.account_name}</td>
              <td className="px-3 py-2">{a.bank_name}</td>
              <td className="px-3 py-2 font-mono text-xs">{a.iban}</td>
              <td className="px-3 py-2 text-right tnum font-semibold">{formatCurrency(a.balance, a.currency)}</td>
              <td className="px-3 py-2 text-xs text-zinc-500">{a.last_statement_at ? new Date(a.last_statement_at).toLocaleString() : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OutstandingCard({ label, data, tone }) {
  const entries = Object.entries(data || {});
  const c = tone === "emerald" ? "text-emerald-700 border-emerald-200" : "text-[#D0021B] border-red-200";
  return (
    <div className={`border ${c} p-4`}>
      <div className="text-xs font-mono uppercase text-zinc-500">{label}</div>
      {entries.length === 0 ? (
        <div className="mt-2 text-sm text-zinc-400">No outstanding items.</div>
      ) : (
        <div className="mt-2 space-y-1">
          {entries.map(([cur, v]) => (
            <div key={cur} className="flex justify-between text-sm">
              <span className="font-mono text-xs text-zinc-500">{cur}</span>
              <span className="tnum font-semibold">{formatCurrency(v, cur)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AccountsPanel({ accounts, reload, cur }) {
  const [form, setForm] = useState({ account_name: "", bank_name: "", iban: "", currency: cur, opening_balance: "", sftp_host: "", sftp_username: "", sftp_path_in: "" });
  const [showSftp, setShowSftp] = useState(false);
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/banking/accounts", {
        ...form,
        opening_balance: Number(form.opening_balance || 0),
        sftp_host: form.sftp_host || null,
        sftp_username: form.sftp_username || null,
        sftp_path_in: form.sftp_path_in || null,
      });
      toast.success("Bank account added");
      setForm({ account_name: "", bank_name: "", iban: "", currency: cur, opening_balance: "", sftp_host: "", sftp_username: "", sftp_path_in: "" });
      setShowSftp(false);
      await reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Remove this bank account?")) return;
    try { await api.delete(`/banking/accounts/${id}`); toast.success("Removed"); await reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="p-6" data-testid="bank-accounts-panel">
      <form onSubmit={create} className="space-y-2 mb-6" data-testid="bank-account-form">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-px bg-zinc-200 border border-zinc-950">
          <input value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })} required placeholder="Account name" data-testid="bank-account-name" className="bg-white px-3 py-2.5 text-sm outline-none" />
          <input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} required placeholder="Bank name" data-testid="bank-account-bank" className="bg-white px-3 py-2.5 text-sm outline-none" />
          <input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value.toUpperCase() })} required placeholder="IBAN" data-testid="bank-account-iban" className="bg-white px-3 py-2.5 text-sm outline-none font-mono text-xs" />
          <input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} required maxLength={3} placeholder="OMR" data-testid="bank-account-currency" className="bg-white px-3 py-2.5 text-sm outline-none tnum" />
          <input type="number" step="0.01" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} required placeholder="Opening balance" data-testid="bank-account-opening-balance" className="bg-white px-3 py-2.5 text-sm outline-none tnum" />
          <button type="submit" className="bg-zinc-950 text-white text-xs font-semibold px-3 hover:bg-[#FF3E00]" data-testid="bank-account-add">ADD</button>
        </div>
        <button type="button" onClick={() => setShowSftp(!showSftp)} className="text-[10px] font-mono text-zinc-500 hover:text-zinc-950" data-testid="bank-sftp-toggle">
          {showSftp ? "▲ HIDE" : "▼ SHOW"} SFTP POLLING CHANNEL (OPTIONAL)
        </button>
        {showSftp && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-200 border border-zinc-300">
            <input value={form.sftp_host} onChange={(e) => setForm({ ...form, sftp_host: e.target.value })} placeholder="sftp.bankmuscat.com" data-testid="bank-account-sftp-host" className="bg-white px-3 py-2.5 text-sm outline-none font-mono text-xs" />
            <input value={form.sftp_username} onChange={(e) => setForm({ ...form, sftp_username: e.target.value })} placeholder="Username" data-testid="bank-account-sftp-username" className="bg-white px-3 py-2.5 text-sm outline-none font-mono text-xs" />
            <input value={form.sftp_path_in} onChange={(e) => setForm({ ...form, sftp_path_in: e.target.value })} placeholder="/statements/inbox/" data-testid="bank-account-sftp-path" className="bg-white px-3 py-2.5 text-sm outline-none font-mono text-xs" />
          </div>
        )}
      </form>

      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-xs uppercase">
            <th className="text-left px-3 py-2">Account</th>
            <th className="text-left px-3 py-2">Bank</th>
            <th className="text-left px-3 py-2">IBAN</th>
            <th className="text-left px-3 py-2">Currency</th>
            <th className="text-right px-3 py-2">Current Balance</th>
            <th />
          </tr>
        </thead>
        <tbody data-testid="bank-accounts-tbody">
          {accounts.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-zinc-400 text-sm">No accounts yet.</td></tr>}
          {accounts.map((a) => (
            <tr key={a.id} className="border-b border-zinc-100 hover:bg-zinc-50">
              <td className="px-3 py-2">{a.account_name}</td>
              <td className="px-3 py-2">{a.bank_name}</td>
              <td className="px-3 py-2 font-mono text-xs">{a.iban}</td>
              <td className="px-3 py-2 text-xs font-mono">{a.currency}</td>
              <td className="px-3 py-2 text-right tnum font-semibold">{formatCurrency(a.current_balance, a.currency)}</td>
              <td className="px-3 py-2"><button onClick={() => del(a.id)} className="text-[10px] font-mono text-zinc-400 hover:text-[#D0021B]" data-testid={`bank-delete-${a.id}`}>REMOVE</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ImportPanel({ accounts }) {
  const [acctId, setAcctId] = useState("");
  const [fmt, setFmt] = useState("mt940");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [emailAddr, setEmailAddr] = useState(null);

  useEffect(() => { api.get("/banking/inbound-email/address").then((r) => setEmailAddr(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file || !acctId) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/banking/import/${fmt}?account_id=${acctId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`Imported ${r.data.transactions} transactions · ${r.data.matched} matched`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="p-6 max-w-2xl" data-testid="import-panel">
      {emailAddr && (
        <div className="border border-[#FF3E00] bg-orange-50 p-4 mb-6" data-testid="statement-email-card">
          <div className="font-mono text-[10px] tracking-widest text-[#FF3E00]">STATEMENT EMAIL INGESTION</div>
          <div className="text-sm mt-1">
            Send MT940 or camt.053 attachments to <span className="font-mono font-semibold select-all" data-testid="statement-email-address">{emailAddr.address}</span> — every attachment is auto-parsed and matched.
          </div>
          <div className="text-[10px] font-mono text-zinc-600 mt-2">Give this address to your bank as the destination for statement delivery.</div>
        </div>
      )}
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="label-sm text-zinc-500">Bank Account</label>
          <select value={acctId} onChange={(e) => setAcctId(e.target.value)} required className="block w-full border-b border-zinc-300 bg-transparent py-2 text-sm outline-none" data-testid="import-account-select">
            <option value="">— select —</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.account_name} · {a.iban}</option>)}
          </select>
        </div>
        <div>
          <label className="label-sm text-zinc-500">Format</label>
          <div className="flex gap-2 mt-1">
            {["mt940", "camt053"].map((f) => (
              <button key={f} type="button" onClick={() => setFmt(f)} className={`text-xs font-mono px-3 py-1.5 border ${fmt === f ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300"}`} data-testid={`import-fmt-${f}`}>
                {f.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="label-sm text-zinc-500">Statement File</label>
          <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} required accept={fmt === "mt940" ? ".txt,.mt940,.sta" : ".xml,.camt"} className="block mt-1 text-sm" data-testid="import-file-input" />
        </div>
        <button type="submit" disabled={busy || !file || !acctId} className="bg-zinc-950 text-white text-xs font-semibold px-5 py-2 hover:bg-[#FF3E00] disabled:opacity-30 flex items-center gap-2" data-testid="import-submit">
          <Upload size={14} /> {busy ? "IMPORTING…" : "IMPORT STATEMENT"}
        </button>
      </form>
      {result && (
        <div className="mt-6 border border-zinc-950 p-4" data-testid="import-result">
          <div className="font-mono text-[10px] tracking-widest text-zinc-500">RESULT</div>
          <div className="mt-2 grid grid-cols-3 gap-4 text-sm">
            <div><div className="text-xs text-zinc-500">Transactions</div><div className="font-display font-black text-xl tnum">{result.transactions}</div></div>
            <div><div className="text-xs text-emerald-600">Matched</div><div className="font-display font-black text-xl tnum text-emerald-600">{result.matched}</div></div>
            <div><div className="text-xs text-[#D0021B]">Unmatched</div><div className="font-display font-black text-xl tnum text-[#D0021B]">{result.unmatched}</div></div>
          </div>
        </div>
      )}
    </div>
  );
}

function TransactionsPanel({ cur, accounts }) {
  const [txns, setTxns] = useState([]);
  const [filter, setFilter] = useState("all");
  const load = async () => {
    const q = filter === "all" ? "" : `?matched=${filter === "matched"}`;
    const r = await api.get(`/banking/transactions${q}`);
    setTxns(r.data);
  };
  useEffect(() => { load().catch(() => {}); }, [filter]);   

  return (
    <div className="p-6" data-testid="transactions-panel">
      <div className="mb-4 flex items-center gap-2">
        {["all", "matched", "unmatched"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`text-[10px] font-mono px-3 py-1 border ${filter === f ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300"}`} data-testid={`txn-filter-${f}`}>
            {f.toUpperCase()}
          </button>
        ))}
      </div>
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-xs uppercase">
            <th className="text-left px-3 py-2">Date</th>
            <th className="text-left px-3 py-2">Direction</th>
            <th className="text-right px-3 py-2">Amount</th>
            <th className="text-left px-3 py-2">Reference</th>
            <th className="text-left px-3 py-2">Description</th>
            <th className="text-left px-3 py-2">Match</th>
            <th className="text-right px-3 py-2">Running Bal</th>
          </tr>
        </thead>
        <tbody data-testid="transactions-tbody">
          {txns.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-zinc-400 text-sm">No transactions.</td></tr>}
          {txns.map((t) => (
            <tr key={t.id} className="border-b border-zinc-100 hover:bg-zinc-50">
              <td className="px-3 py-2 tnum text-xs text-zinc-500">{t.date}</td>
              <td className="px-3 py-2"><span className={`text-[10px] font-mono px-1.5 py-0.5 ${t.is_credit ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-[#D0021B]"}`}>{t.is_credit ? "CR" : "DR"}</span></td>
              <td className={`px-3 py-2 text-right tnum font-semibold ${t.is_credit ? "text-emerald-700" : "text-[#D0021B]"}`}>{formatCurrency(t.amount, t.currency || cur)}</td>
              <td className="px-3 py-2 font-mono text-xs">{t.reference || "—"}</td>
              <td className="px-3 py-2 text-xs text-zinc-600 max-w-[300px] truncate" title={t.description}>{t.description || "—"}</td>
              <td className="px-3 py-2">
                {t.matched ? (
                  <span className="text-[10px] font-mono px-1.5 py-0.5 bg-emerald-50 text-emerald-700" title={`Confidence ${t.match?.confidence || 0}%`}>{t.match?.kind?.toUpperCase()} {t.match?.confidence}%</span>
                ) : (
                  <span className="text-[10px] font-mono px-1.5 py-0.5 bg-amber-50 text-amber-700">UNMATCHED</span>
                )}
              </td>
              <td className="px-3 py-2 text-right tnum text-xs">{formatCurrency(t.running_balance, t.currency || cur)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-3 text-xs text-zinc-500">{accounts.length} bank account(s) on file · {txns.length} transactions shown.</div>
    </div>
  );
}
